<script setup>
import {computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import CustomerChatPreQuestionFlow from './CustomerChatPreQuestionFlow.vue';
import CustomerChatTypingIndicator from './CustomerChatTypingIndicator.vue';
import {chatRequest, connectRealtime, loadCapability} from './api';
import {
    browserNotificationPermission,
    browserNotificationsSupported,
    closeAllBrowserMessageNotifications,
    closeBrowserMessageNotification,
    isPageInactive,
    readBrowserNotificationPreference,
    requestBrowserNotificationPermission,
    showBrowserMessageNotification,
    storeBrowserNotificationPreference,
} from './browserNotifications';
import {shouldSendOnEnter} from './keyboard';
import {createTypingExpiry, createTypingReporter} from './typing';

const CUSTOMER_NOTIFICATION_STORAGE_KEY = 'customer-chat.customer.browser-notifications.enabled';
const SUPPORTED_LANGUAGES = ['ko', 'en', 'zh', 'ja'];

function normalizeSupportedLocale(value) {
    const normalized = String(value || 'ko').replace('_', '-').toLowerCase();
    const language = normalized === 'kr' || normalized === 'ko-kr'
        ? 'ko'
        : normalized.split('-', 1)[0];

    return SUPPORTED_LANGUAGES.includes(language) ? language : 'ko';
}

function storefrontTranslationLocale(value) {
    if (value === 'ko') return 'kr';
    if (value === 'zh') return 'zh-CN';

    return value;
}

const props = defineProps({
    locale: {type: String, default: 'kr'},
    preview: {type: Boolean, default: false},
    previewCapability: {type: Object, default: null},
});
const emit = defineEmits(['hide', 'show']);
const instance = getCurrentInstance();
const globalTranslate = instance?.proxy?.$t || instance?.appContext.config.globalProperties.$t;
const widgetLocale = ref(normalizeSupportedLocale(props.locale));
const $t = (key, options = {}) => globalTranslate?.(key, {
    ...options,
    locale: storefrontTranslationLocale(widgetLocale.value),
}) || key;
const t = $t;
const opened = ref(props.preview);
const loading = ref(false);
const sending = ref(false);
const error = ref('');
const capability = ref(props.preview ? props.previewCapability : null);
const realtimeConnected = ref(false);
const conversation = ref(null);
const introConversations = ref([]);
const pendingRating = ref(null);
const recentClosed = ref([]);
const authenticated = ref(false);
const customerProfile = ref(null);
const showStartForm = ref(false);
const showPreQuestions = ref(false);
const draft = ref('');
const form = ref({
    message: '',
    customer_name: '',
    contact_phone: '',
    contact_email: '',
    locale: widgetLocale.value,
    notification_consent: true,
});
const rating = ref(5);
const ratingComment = ref('');
const submittingRating = ref(false);
const ratingSubmitted = ref(false);
const continuationDraft = ref('');
const messageArea = ref(null);
const imageInput = ref(null);
const continuationImageInput = ref(null);
const administratorTyping = ref(false);
const unreadCount = ref(0);
const notificationPermission = ref(browserNotificationPermission());
const browserNotificationsEnabled = ref(readBrowserNotificationPreference(
    CUSTOMER_NOTIFICATION_STORAGE_KEY,
    notificationPermission.value
));
const browserNotifiedMessageIds = new Set();
let pollTimer = null;
let heartbeatTimer = null;
let disconnectRealtime = null;
let disconnectOverviewRealtime = null;
let overviewConversationId = null;
let capabilityTimer = null;

const administratorTypingExpiry = createTypingExpiry(active => {
    administratorTyping.value = active;
});
const customerTypingReporter = createTypingReporter(active => {
    if (
        props.preview
        || !conversation.value
        || conversation.value.status === 'closed'
        || conversation.value.channel_capabilities?.typing !== true
        || !capability.value?.realtime?.enabled
        || !realtimeConnected.value
    ) return null;

    return chatRequest(
        `/api/customer-chat/conversations/${conversation.value.public_id}/typing`,
        {method: 'POST', body: JSON.stringify({active})}
    );
});

const messages = computed(() => conversation.value?.messages || []);
const messageLayoutVersion = computed(() => messages.value
    .map(message => `${message.id}:${message.body_parts?.length || 0}:${message.attachment ? 1 : 0}`)
    .join('|'));
const isClosed = computed(() => conversation.value?.status === 'closed');
const hasActiveConversation = computed(() => Boolean(conversation.value) && !isClosed.value);
const hasUnresolvedConversation = computed(() => (
    hasActiveConversation.value || introConversations.value.length > 0
));
const displayStartForm = computed(() => showStartForm.value);
const preQuestionTree = computed(() => capability.value?.pre_questions || null);
const isKoreanLanguage = computed(() => form.value.locale === 'ko');
const canStart = computed(() => {
    if (!form.value.message.trim()) return false;
    if (authenticated.value) {
        return isKoreanLanguage.value || Boolean(form.value.contact_email.trim());
    }
    if (!form.value.customer_name.trim()) return false;
    if (!isKoreanLanguage.value) return Boolean(form.value.contact_email.trim());
    return Boolean(form.value.contact_phone.trim() || form.value.contact_email.trim());
});
const unreadLabel = computed(() => unreadCount.value > 99 ? '99+' : String(unreadCount.value));
const notificationStatusLabel = computed(() => ({
    granted: browserNotificationsEnabled.value ? t('켜짐') : t('꺼짐'),
    denied: t('브라우저에서 차단됨'),
    default: t('꺼짐'),
    unsupported: t('지원하지 않는 브라우저'),
}[notificationPermission.value] || t('꺼짐')));
const notificationSwitchDisabled = computed(() => (
    props.preview
    || !browserNotificationsSupported()
    || notificationPermission.value === 'denied'
    || notificationPermission.value === 'unsupported'
));
const notificationPermissionHint = computed(() => ({
    denied: t('브라우저 사이트 설정에서 알림 권한을 허용해 주세요.'),
    unsupported: t('이 브라우저에서는 알림을 지원하지 않습니다.'),
}[notificationPermission.value] || ''));

function normalizedLocale() {
    return widgetLocale.value;
}

function updateLanguage(locale) {
    widgetLocale.value = normalizeSupportedLocale(locale);
    form.value.locale = widgetLocale.value;
    if (!isKoreanLanguage.value) form.value.contact_phone = '';
}

function beginStartFlow() {
    if (props.preview) return;

    showStartForm.value = !preQuestionTree.value?.enabled;
    showPreQuestions.value = Boolean(preQuestionTree.value?.enabled);
}

function continueFromPreQuestions() {
    showPreQuestions.value = false;
    showStartForm.value = true;
}

function messageParts(message) {
    if (message.body_parts?.length) return message.body_parts;
    if (message.body) return [{type: 'original', locale: null, text: message.body}];

    return [];
}

function attachmentUrl(message) {
    return `/api/customer-chat/conversations/${conversation.value.public_id}/messages/${message.id}/attachment`;
}

function mergeMessages(incoming) {
    if (!conversation.value) return [];
    const byId = new Map(messages.value.map(message => [message.id, message]));
    const added = [];

    incoming.forEach(message => {
        const existing = byId.get(message.id);
        if (existing) {
            Object.assign(existing, message);
            return;
        }
        conversation.value.messages.push(message);
        byId.set(message.id, message);
        added.push(message);
    });
    conversation.value.messages.sort((left, right) => left.id - right.id);

    return added;
}

function removeMessages(messageIds) {
    if (!conversation.value || !messageIds?.length) return [];
    const recalledIds = new Set(messageIds.map(Number));
    const removed = messages.value.filter(message => recalledIds.has(Number(message.id)));
    conversation.value.messages = messages.value.filter(message => !recalledIds.has(Number(message.id)));
    messageIds.forEach(closeBrowserMessageNotification);

    return removed;
}

function applyReadReceipts(payload) {
    if (conversation.value?.public_id !== payload.conversation_public_id) return;
    const readMessageIds = new Set((payload.message_ids || []).map(Number));
    messages.value.forEach(message => {
        if (readMessageIds.has(Number(message.id))) {
            message.recipient_read_at = payload.read_at;
        }
    });
}

function applyReadThrough(readThrough) {
    messages.value.forEach(message => {
        const senderReadThrough = Number(readThrough?.[message.sender_type] || 0);
        if (senderReadThrough && Number(message.id) <= senderReadThrough && !message.recipient_read_at) {
            message.recipient_read_at = new Date().toISOString();
        }
    });
}

function isCustomerChatActive() {
    return opened.value && !isPageInactive();
}

function openNotificationConversation(conversationId) {
    void (async () => {
        await openTalk();
        if (conversation.value?.public_id !== conversationId) {
            await openConversation(conversationId);
        }
    })();
}

function recordBrowserNotification(message) {
    return chatRequest(
        `/api/customer-chat/conversations/${message.conversation_public_id}/messages/${message.id}/browser-notification`,
        {method: 'POST'}
    ).catch(() => null);
}

async function notifyCustomerMessage(message, mobilePushAvailable = message.mobile_push_available) {
    if (!browserNotificationsEnabled.value
        || message.sender_type !== 'system'
        || message.notification_consent === false
        || message.recipient_read_at
        || isCustomerChatActive()
        || mobilePushAvailable
        || browserNotifiedMessageIds.has(Number(message.id))
    ) return;
    const recorded = await recordBrowserNotification(message);
    if (!recorded?.recorded) return;

    const notification = showBrowserMessageNotification({
        messageId: message.id,
        title: t('캔들웍스 상담'),
        body: t('상담원 답변이 도착했습니다.'),
        onClick: () => openNotificationConversation(message.conversation_public_id),
    });
    if (notification) {
        browserNotifiedMessageIds.add(Number(message.id));
    }
}

function syncBrowserNotificationState() {
    notificationPermission.value = browserNotificationPermission();
    browserNotificationsEnabled.value = readBrowserNotificationPreference(
        CUSTOMER_NOTIFICATION_STORAGE_KEY,
        notificationPermission.value
    );
}

async function setCustomerBrowserNotificationsEnabled(enabled) {
    if (props.preview) return;

    if (!enabled) {
        browserNotificationsEnabled.value = false;
        storeBrowserNotificationPreference(CUSTOMER_NOTIFICATION_STORAGE_KEY, false);
        closeAllBrowserMessageNotifications();

        return;
    }

    let permission = browserNotificationPermission();
    if (permission === 'default') permission = await requestBrowserNotificationPermission();
    notificationPermission.value = permission;
    browserNotificationsEnabled.value = permission === 'granted';
    if (browserNotificationsEnabled.value) {
        storeBrowserNotificationPreference(CUSTOMER_NOTIFICATION_STORAGE_KEY, true);
    }
}

async function openTalk() {
    opened.value = true;
    emit('show', true);
    if (props.preview) return;

    if (!capability.value) {
        await initialize();
        startCapabilityRefresh();
        return;
    }
    await refreshCapability();
    startCapabilityRefresh();
    if (conversation.value) {
        startHeartbeat();
        await markRead();
        scrollToBottom();
    }
}

function closePanel() {
    if (props.preview) return;

    opened.value = false;
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    emit('hide', true);
    clearInterval(capabilityTimer);
    capabilityTimer = null;
    if (ratingSubmitted.value) {
        ratingSubmitted.value = false;
        conversation.value = null;
        rating.value = 5;
        ratingComment.value = '';
        attachOverviewRealtime();
    }
}

async function initialize() {
    loading.value = true;
    error.value = '';
    try {
        capability.value = await loadCapability();
        updateLanguage(props.locale);
        await loadOverview();
        const resumeId = new URLSearchParams(window.location.search).get('customer_chat');
        if (resumeId) await openConversation(resumeId);
    } catch (exception) {
        error.value = exception.message;
    } finally {
        loading.value = false;
    }
}

async function refreshCapability() {
    const previous = Boolean(capability.value?.realtime?.enabled);
    capability.value = await loadCapability();
    if (showPreQuestions.value && !capability.value?.pre_questions?.enabled) {
        continueFromPreQuestions();
    }
    if (previous !== Boolean(capability.value?.realtime?.enabled)) {
        if (conversation.value) attachRealtime();
        else attachOverviewRealtime();
    }
}

function startCapabilityRefresh() {
    clearInterval(capabilityTimer);
    capabilityTimer = setInterval(() => refreshCapability().catch(() => null), 15_000);
}

function receiveConversationUpdate(update) {
    if (!conversation.value || update.public_id !== conversation.value.public_id) return;

    const currentMessages = conversation.value.messages || [];
    conversation.value = {
        ...conversation.value,
        ...update,
        messages: currentMessages,
    };

    if (isClosed.value) {
        customerTypingReporter.stop();
        administratorTypingExpiry.clear();
        stopPolling();
        clearInterval(heartbeatTimer);
        disconnectRealtime?.();
        disconnectRealtime = null;
    }
}

async function loadOverview() {
    const response = await chatRequest('/api/customer-chat/conversations');
    introConversations.value = response.data || [];
    pendingRating.value = response.pending_rating || null;
    recentClosed.value = response.recent_closed || [];
    authenticated.value = Boolean(response.authenticated);
    customerProfile.value = response.customer || null;
    if (customerProfile.value) {
        form.value.customer_name = customerProfile.value.name || '';
        form.value.contact_phone = customerProfile.value.phone || '';
        form.value.contact_email = customerProfile.value.email || '';
    }
    unreadCount.value = introConversations.value.reduce(
        (total, item) => total + Number(item.unread_count || 0),
        0
    );
    (response.browser_notification_messages || []).forEach(notifyCustomerMessage);
    if (!conversation.value) attachOverviewRealtime();
}

async function openConversation(conversationId, reconnect = true) {
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    loading.value = true;
    error.value = '';
    ratingSubmitted.value = false;
    try {
        const previousId = conversation.value?.public_id;
        const response = await chatRequest(`/api/customer-chat/conversations/${conversationId}`);
        conversation.value = response.data;
        updateLanguage(conversation.value?.locale || props.locale);
        showStartForm.value = false;
        showPreQuestions.value = false;
        if (reconnect || previousId !== conversationId || !disconnectRealtime || isClosed.value) {
            attachRealtime();
        }
        startHeartbeat();
        if (opened.value) await markRead();
        scrollToBottom();
    } catch (exception) {
        error.value = exception.message;
    } finally {
        loading.value = false;
    }
}

async function returnToIntro() {
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    disconnectRealtime?.();
    disconnectRealtime = null;
    stopPolling();
    clearInterval(heartbeatTimer);
    ratingSubmitted.value = false;
    conversation.value = null;
    showStartForm.value = false;
    showPreQuestions.value = false;
    await loadOverview();
}

async function refreshMessages() {
    if (!conversation.value || isClosed.value) return;
    const lastId = messages.value.at(-1)?.id || 0;
    const response = await chatRequest(
        `/api/customer-chat/conversations/${conversation.value.public_id}/messages?after_id=${lastId}`
    );
    if (response.conversation) receiveConversationUpdate(response.conversation);
    applyReadThrough(response.read_through || {});
    const recalled = removeMessages(response.recalled_ids || []);
    const recalledUnreadCount = recalled.filter(message => (
        ['admin', 'system'].includes(message.sender_type) && !message.recipient_read_at
    )).length;
    if (recalledUnreadCount) {
        unreadCount.value = Math.max(0, unreadCount.value - recalledUnreadCount);
    }
    const added = mergeMessages(Array.isArray(response.data) ? response.data : []);
    if (added.length) {
        const unreadAdminMessages = added.filter(message => (
            ['admin', 'system'].includes(message.sender_type) && !message.recipient_read_at
        ));
        if (isCustomerChatActive() && unreadAdminMessages.length) {
            await markRead();
        } else {
            unreadCount.value += unreadAdminMessages.length;
            unreadAdminMessages.forEach(message => notifyCustomerMessage(message));
        }
        scrollToBottom();
    }
    (response.browser_fallback_message_ids || []).forEach(messageId => {
        const message = messages.value.find(item => Number(item.id) === Number(messageId));
        if (message) notifyCustomerMessage({...message, mobile_push_available: false}, false);
    });
}

async function receiveRealtimeMessage(message) {
    if (!conversation.value || isClosed.value) return;
    if (message.conversation_public_id !== conversation.value.public_id) return;

    const added = mergeMessages([message]);
    if (!added.length) return;
    const unreadAdminMessages = added.filter(item => (
        ['admin', 'system'].includes(item.sender_type) && !item.recipient_read_at
    ));
    if (isCustomerChatActive() && unreadAdminMessages.length) {
        await markRead();
    } else {
        unreadCount.value += unreadAdminMessages.length;
        unreadAdminMessages.forEach(notifyCustomerMessage);
    }
    scrollToBottom();
}

function receiveTranslatedMessages(payload) {
    mergeMessages(payload.messages || []);
}

async function receiveOverviewRealtimeMessage(message) {
    notifyCustomerMessage(message);
    await loadOverview();
}

async function receiveMobilePushUnavailable(payload) {
    if (conversation.value?.public_id === payload.conversation_public_id) {
        const message = messages.value.find(item => Number(item.id) === Number(payload.message_id));
        if (message) {
            await notifyCustomerMessage({...message, mobile_push_available: false}, false);

            return;
        }
    }

    await loadOverview();
}

function receiveMessageRecall(payload) {
    if (conversation.value?.public_id !== payload.conversation_public_id) {
        closeBrowserMessageNotification(payload.message_id);
        loadOverview();

        return;
    }

    const removed = removeMessages([payload.message_id]);
    const unreadRemoved = removed.filter(message => (
        ['admin', 'system'].includes(message.sender_type) && !message.recipient_read_at
    )).length;
    unreadCount.value = Math.max(0, unreadCount.value - unreadRemoved);
}

async function startConversation() {
    if (sending.value || !canStart.value) return;
    sending.value = true;
    error.value = '';
    try {
        const response = await chatRequest('/api/customer-chat/conversations', {
            method: 'POST',
            body: JSON.stringify(form.value),
        });
        conversation.value = response.data;
        unreadCount.value = Number(conversation.value.unread_count || 0);
        form.value.message = '';
        showStartForm.value = false;
        showPreQuestions.value = false;
        await loadOverview();
        attachRealtime();
        startHeartbeat();
        scrollToBottom();
    } catch (exception) {
        error.value = exception.message;
    } finally {
        sending.value = false;
    }
}

async function sendMessage() {
    if (sending.value || !draft.value.trim() || !conversation.value) return;
    sending.value = true;
    customerTypingReporter.stop();
    error.value = '';
    try {
        const response = await chatRequest(
            `/api/customer-chat/conversations/${conversation.value.public_id}/messages`,
            {method: 'POST', body: JSON.stringify({message: draft.value})}
        );
        mergeMessages([response.data]);
        draft.value = '';
        scrollToBottom();
    } catch (exception) {
        error.value = exception.message;
    } finally {
        sending.value = false;
    }
}

async function continueConversation(image = null) {
    if (sending.value || !conversation.value || !isClosed.value) return;
    const body = continuationDraft.value.trim();
    if (!body && !image) return;

    const previousId = conversation.value.public_id;
    sending.value = true;
    error.value = '';
    try {
        const options = image
            ? {method: 'POST', body: (() => {
                const formData = new FormData();
                formData.append('image', image);
                return formData;
            })()}
            : {method: 'POST', body: JSON.stringify({message: body})};
        const response = await chatRequest(
            `/api/customer-chat/conversations/${previousId}/continue`,
            options
        );
        conversation.value = response.data;
        continuationDraft.value = '';
        ratingSubmitted.value = false;
        await loadOverview();
        attachRealtime();
        startHeartbeat();
        scrollToBottom();
    } catch (exception) {
        error.value = exception?.message || String(exception);
    } finally {
        sending.value = false;
    }
}

function chooseContinuationImage() {
    if (!sending.value) continuationImageInput.value?.click();
}

async function continueWithImage(event) {
    const input = event.target;
    const image = input.files?.[0];
    if (!image) return;
    await continueConversation(image);
    input.value = '';
}

function chooseImage() {
    if (!sending.value) imageInput.value?.click();
}

async function sendImage(event) {
    const input = event.target;
    const image = input.files?.[0];
    if (!image || sending.value || !conversation.value) return;

    const conversationId = conversation.value.public_id;
    const formData = new FormData();
    formData.append('image', image);
    sending.value = true;
    error.value = '';
    try {
        const response = await chatRequest(
            `/api/customer-chat/conversations/${conversationId}/messages`,
            {method: 'POST', body: formData}
        );
        if (conversation.value?.public_id === conversationId) {
            mergeMessages([response.data]);
            scrollToBottom();
        }
    } catch (exception) {
        error.value = exception.message;
    } finally {
        input.value = '';
        sending.value = false;
    }
}

function handleDraftKeydown(event) {
    if (!shouldSendOnEnter(event)) return;
    event.preventDefault();
    sendMessage();
}

function handleDraftFocus() {
    if (realtimeConnected.value) {
        customerTypingReporter.update(Boolean(draft.value.trim()));
    }
}

function handleDraftBlur() {
    customerTypingReporter.stop();
}

function receiveTypingStatus(payload) {
    if (
        payload?.actor_type !== 'admin'
        || payload?.conversation_public_id !== conversation.value?.public_id
    ) return;
    administratorTypingExpiry.receive(payload);
}

async function closeConversation() {
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    const response = await chatRequest(
        `/api/customer-chat/conversations/${conversation.value.public_id}/close`,
        {method: 'POST'}
    );
    conversation.value = {...conversation.value, ...response.data};
    attachRealtime();
    await loadOverview();
}

async function submitRating() {
    if (submittingRating.value || !conversation.value) return;
    submittingRating.value = true;
    error.value = '';

    try {
        const response = await chatRequest(
            `/api/customer-chat/conversations/${conversation.value.public_id}/rating`,
            {
                method: 'POST',
                body: JSON.stringify({rating: rating.value, comment: ratingComment.value}),
            }
        );
        conversation.value = {...conversation.value, ...response.data};
        ratingSubmitted.value = opened.value;
        await loadOverview();
        if (!opened.value) {
            conversation.value = null;
            attachOverviewRealtime();
        }
    } catch (exception) {
        error.value = exception?.message || String(exception);
    } finally {
        submittingRating.value = false;
    }
}

async function markRead() {
    if (!conversation.value) return;
    await chatRequest(`/api/customer-chat/conversations/${conversation.value.public_id}/read`, {method: 'POST'});
    const readAt = new Date().toISOString();
    messages.value.forEach(message => {
        if (['admin', 'system'].includes(message.sender_type) && !message.recipient_read_at) {
            message.recipient_read_at = readAt;
        }
        if (['admin', 'system'].includes(message.sender_type)) closeBrowserMessageNotification(message.id);
    });
    unreadCount.value = 0;
}

function attachRealtime() {
    realtimeConnected.value = false;
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    disconnectOverviewRealtime?.();
    disconnectOverviewRealtime = null;
    overviewConversationId = null;
    disconnectRealtime?.();
    disconnectRealtime = null;
    stopPolling();
    if (!conversation.value || isClosed.value) return;
    startPolling();
    disconnectRealtime = connectRealtime(
        capability.value,
        `private-customer-chat.conversation.${conversation.value.public_id}`,
        {
            'customer-chat.message.created': receiveRealtimeMessage,
            'customer-chat.mobile-push.unavailable': receiveMobilePushUnavailable,
            'customer-chat.message.recalled': receiveMessageRecall,
            'customer-chat.messages.read': applyReadReceipts,
            'customer-chat.messages.translated': receiveTranslatedMessages,
            'customer-chat.typing.changed': receiveTypingStatus,
            'customer-chat.conversation.updated': receiveConversationUpdate,
        },
        {
            onConnected: () => {
                realtimeConnected.value = true;
                stopPolling();
                refreshMessages();
                customerTypingReporter.update(Boolean(draft.value.trim()));
            },
            onDisconnected: () => {
                realtimeConnected.value = false;
                customerTypingReporter.stop();
                administratorTypingExpiry.clear();
                startPolling();
            },
            onError: () => {
                realtimeConnected.value = false;
                customerTypingReporter.stop();
                administratorTypingExpiry.clear();
                startPolling();
            },
        }
    );
}

function attachOverviewRealtime() {
    realtimeConnected.value = false;
    customerTypingReporter.stop();
    administratorTypingExpiry.clear();
    const conversationId = introConversations.value[0]?.public_id;
    if (!conversationId) {
        disconnectOverviewRealtime?.();
        disconnectOverviewRealtime = null;
        overviewConversationId = null;
        stopPolling();
        return;
    }
    if (overviewConversationId === conversationId && disconnectOverviewRealtime) return;

    disconnectOverviewRealtime?.();
    stopPolling();
    overviewConversationId = conversationId;
    startPolling();
    disconnectOverviewRealtime = connectRealtime(
        capability.value,
        `private-customer-chat.conversation.${conversationId}`,
        {
            'customer-chat.message.created': receiveOverviewRealtimeMessage,
            'customer-chat.mobile-push.unavailable': receiveMobilePushUnavailable,
            'customer-chat.message.recalled': receiveMessageRecall,
            'customer-chat.messages.read': applyReadReceipts,
            'customer-chat.messages.translated': receiveTranslatedMessages,
            'customer-chat.conversation.updated': loadOverview,
        },
        {
            onConnected: () => {
                stopPolling();
                loadOverview();
            },
            onDisconnected: startPolling,
        }
    );
}

function startPolling() {
    if (pollTimer) return;
    if (conversation.value && isClosed.value) return;
    if (!conversation.value && !introConversations.value.length) return;
    const seconds = capability.value?.polling?.fallback_seconds || 5;
    recoverCustomerGap();
    pollTimer = setInterval(recoverCustomerGap, seconds * 1000);
}

function recoverCustomerGap() {
    return conversation.value ? refreshMessages() : loadOverview();
}

function stopPolling() {
    clearInterval(pollTimer);
    pollTimer = null;
}

function startHeartbeat() {
    clearInterval(heartbeatTimer);
    const heartbeat = async () => {
        if (!conversation.value || !isCustomerChatActive() || isClosed.value) return;
        await chatRequest(
            `/api/customer-chat/conversations/${conversation.value.public_id}/heartbeat`,
            {method: 'POST'}
        ).catch(() => null);
    };
    heartbeat();
    heartbeatTimer = setInterval(heartbeat, (capability.value?.presence?.heartbeat_seconds || 20) * 1000);
}

function handlePageActivation() {
    syncBrowserNotificationState();
    if (!isCustomerChatActive() || !conversation.value) return;
    startHeartbeat();
    void markRead();
}

function scrollToBottom() {
    nextTick(() => {
        if (messageArea.value) messageArea.value.scrollTop = messageArea.value.scrollHeight;
    });
}

watch(
    [() => conversation.value?.public_id, messageLayoutVersion],
    scrollToBottom,
    {flush: 'post'}
);

watch(
    () => props.previewCapability,
    value => {
        if (props.preview) capability.value = value;
    },
    {deep: true}
);

watch(() => props.locale, updateLanguage);

watch(draft, value => {
    if (realtimeConnected.value) {
        customerTypingReporter.update(Boolean(value.trim()));
    } else {
        customerTypingReporter.stop();
    }
});

function formatDate(value) {
    return value
        ? new Intl.DateTimeFormat(normalizedLocale(), {dateStyle: 'short', timeStyle: 'short'}).format(new Date(value))
        : '-';
}

onMounted(() => {
    if (props.preview) {
        updateLanguage(props.locale);
        opened.value = true;
        return;
    }

    window.addEventListener('focus', handlePageActivation);
    document.addEventListener('visibilitychange', handlePageActivation);
    if (new URLSearchParams(window.location.search).has('customer_chat')) {
        openTalk();
    } else {
        initialize();
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('focus', handlePageActivation);
    document.removeEventListener('visibilitychange', handlePageActivation);
    stopPolling();
    clearInterval(heartbeatTimer);
    disconnectRealtime?.();
    disconnectOverviewRealtime?.();
    clearInterval(capabilityTimer);
    customerTypingReporter.dispose();
    administratorTypingExpiry.dispose();
});

defineExpose({openTalk});
</script>

<template>
  <div v-if="!preview" class="hidden lg:flex fixed right-4 bottom-4 z-[200] flex-col items-end gap-2">
    <button
      v-if="!opened && hasUnresolvedConversation"
      type="button"
      data-testid="customer-chat-active-status"
      class="flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm text-stone-700 shadow-lg border border-stone-200"
      @click="openTalk"
    >
      <span class="h-2 w-2 rounded-full bg-green-500" />
      <span>{{ t('상담 진행 중') }}</span>
      <span
        v-if="unreadCount"
        class="min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-xs font-bold text-white"
        :aria-label="t('읽지 않은 메시지 {count}개', {count: unreadCount})"
      >{{ unreadLabel }}</span>
    </button>
    <slot name="before-launcher" />
    <button
      type="button"
      data-testid="customer-chat-launcher"
      class="relative flex w-14 h-14 rounded-full bg-stone-600 text-white shadow-lg items-center justify-center cursor-pointer"
      :title="t('채팅 상담')"
      :aria-label="unreadCount ? t('채팅 상담, 읽지 않은 메시지 {count}개', {count: unreadCount}) : t('채팅 상담')"
      @click="openTalk"
    >
      <span class="text-2xl">💬</span>
      <span
        v-if="unreadCount"
        class="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white"
      >{{ unreadLabel }}</span>
    </button>
  </div>
  <div
    v-if="opened"
    :data-testid="preview ? 'customer-chat-widget-preview' : undefined"
    :class="preview ? 'relative w-full' : 'fixed inset-0 z-[200] bg-black/30 flex items-center sm:items-center justify-center sm:p-4'"
  >
    <section
      class="cw-chat-panel bg-white w-full flex flex-col overflow-hidden"
      :class="preview ? 'cw-chat-panel--preview max-w-none rounded-xl border border-stone-200 shadow-lg' : 'lg:max-w-2xl sm:w-[390px] shadow-2xl sm:rounded-2xl'"
    >
      <header class="bg-stone-600 text-white px-4 py-3 flex justify-between items-center">
        <div class="flex items-center gap-2">
          <button v-if="conversation" type="button" class="text-xl" :aria-label="t('상담 목록')" @click="returnToIntro">‹</button>
          <div><strong>{{ t('상담하기') }}</strong><p class="text-xs text-stone-300">{{ t('실시간 채팅 · 비회원 가능') }}</p></div>
        </div>
        <button type="button" class="text-2xl" :aria-label="t('닫기')" :disabled="preview" @click="closePanel">×</button>
      </header>

      <div v-if="loading" class="m-auto text-stone-500">{{ t('상담을 불러오는 중입니다.') }}</div>
      <div v-else-if="!conversation" class="p-4 overflow-y-auto space-y-3">
        <div>
          <div class="flex items-center justify-between gap-2">
            <h2 class="font-semibold">{{ t('최근 진행 중인 상담') }}</h2>
            <span v-if="capability?.realtime?.enabled && capability?.operating_hours?.is_open !== false" data-testid="customer-chat-realtime-answering" class="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">{{ t('실시간 답변 중') }}</span>
          </div>
          <p class="text-xs text-stone-500">{{ t('종료되지 않은 상담을 선택해 이어갈 수 있습니다.') }}</p>
          <div class="mt-3 rounded-xl bg-stone-50 p-3 text-xs text-stone-600">
            <strong>{{ t('고객센터 운영시간') }}</strong>
            <p class="mt-1">{{ t('평일') }} {{ capability?.support?.weekday_hours || '10:00~18:00' }}<template v-if="capability?.support?.lunch_hours"> · {{ t('점심시간') }} {{ capability.support.lunch_hours }}</template></p>
            <p>{{ t('주말, 공휴일 휴무') }}</p>
          </div>
          <p
            v-if="capability?.operating_hours?.is_open === false"
            data-testid="customer-chat-outside-hours-message"
            class="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900"
          >{{ t(capability?.operating_hours?.outside_message || '죄송합니다. 지금은 상담 운영시간이 아닙니다.  다음 운영시간까지 답변이 늦어질 수 있습니다. 궁금한 내용을 편하게 남겨두시면 놓치지 않고 답변드리겠습니다.') }}</p>
        </div>
        <div data-testid="customer-chat-browser-notification-setting" class="flex items-center justify-between gap-3 rounded-xl border border-stone-200 p-3">
          <div class="min-w-0">
            <strong class="text-sm text-stone-800">{{ t('브라우저 답변 알림') }}</strong>
            <p class="text-xs text-stone-500">{{ notificationStatusLabel }}</p>
            <p v-if="notificationPermissionHint" class="mt-1 text-xs text-amber-700">{{ notificationPermissionHint }}</p>
          </div>
          <slot
            name="browser-notification-switch"
            :enabled="browserNotificationsEnabled"
            :disabled="notificationSwitchDisabled"
            :label="t('브라우저 답변 알림 {status}', {status: notificationStatusLabel})"
            :toggle="setCustomerBrowserNotificationsEnabled"
          >
            <button
              type="button"
              role="switch"
              data-testid="customer-chat-browser-notification-fallback"
              class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full ring-1 ring-inset transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              :class="browserNotificationsEnabled ? 'bg-stone-700 ring-stone-800' : 'bg-stone-200 ring-stone-300'"
              :aria-checked="browserNotificationsEnabled"
              :aria-label="t('브라우저 답변 알림 {status}', {status: notificationStatusLabel})"
              :disabled="notificationSwitchDisabled"
              @click="setCustomerBrowserNotificationsEnabled(!browserNotificationsEnabled)"
            >
              <span
                aria-hidden="true"
                class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
                :class="browserNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'"
              />
            </button>
          </slot>
        </div>
        <div v-if="introConversations.length" class="space-y-2">
          <button
            v-for="item in introConversations"
            :key="item.public_id"
            type="button"
            data-testid="customer-chat-intro-conversation"
            class="w-full rounded-xl border border-stone-200 p-3 text-left hover:bg-stone-50"
            @click="openConversation(item.public_id)"
          >
            <span class="flex items-center justify-between gap-2">
              <strong class="text-sm">{{ item.status === 'waiting' ? t('상담원 연결 대기') : t('상담 진행 중') }}</strong>
              <span v-if="item.unread_count" class="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">{{ item.unread_count }}</span>
            </span>
            <span class="mt-1 block text-xs text-stone-500">{{ t('최근 대화') }} {{ formatDate(item.last_activity_at) }}</span>
          </button>
        </div>
        <p v-else class="rounded-xl bg-stone-50 p-3 text-sm text-stone-500">{{ t('진행 중인 상담이 없습니다.') }}</p>

        <button
          v-if="pendingRating"
          type="button"
          class="w-full rounded-xl border border-amber-200 bg-amber-50 p-3 text-left text-sm"
          @click="openConversation(pendingRating.public_id)"
        >
          {{ t('최근 종료 상담을 평가해 주세요.') }}
        </button>

        <button
          v-for="item in recentClosed"
          :key="`closed-${item.public_id}`"
          type="button"
          data-testid="customer-chat-recent-closed"
          class="w-full rounded-xl border border-stone-200 bg-stone-50 p-3 text-left text-sm"
          @click="openConversation(item.public_id)"
        >
          <strong>{{ t('이전 상담 이어서 문의하기') }}</strong>
          <span class="mt-1 block text-xs text-stone-500">{{ formatDate(item.closed_at) }}</span>
        </button>

        <button
          v-if="!introConversations.length && !displayStartForm && !showPreQuestions"
          type="button"
          class="cw-chat-primary w-full"
          :disabled="preview"
          @click="beginStartFlow"
        >{{ t('채팅 시작하기') }}</button>

        <CustomerChatPreQuestionFlow
          v-if="showPreQuestions && preQuestionTree"
          :tree="preQuestionTree"
          @continue="continueFromPreQuestions"
        />

        <div v-if="displayStartForm" class="space-y-3 border-t pt-3">
          <p class="text-sm text-stone-600">{{ t('문의 내용을 남기면 상담원이 이 채팅에서 답변합니다.') }}</p>
          <label class="block text-xs font-medium text-stone-600">{{ t('언어') }} <span class="text-red-600">{{ t('필수') }}</span>
            <iconic-select
              data-testid="customer-chat-language"
              class="mt-1 h-12 overflow-hidden rounded-lg border border-stone-200"
              :value="form.locale"
              @update:value="updateLanguage"
            >
              <iconic-option value="ko">한국어</iconic-option>
              <iconic-option value="en">English</iconic-option>
              <iconic-option value="zh">中文</iconic-option>
              <iconic-option value="ja">日本語</iconic-option>
            </iconic-select>
          </label>
          <div v-if="authenticated" class="rounded-xl bg-stone-50 p-3 text-sm text-stone-600">
            <strong class="text-stone-800">{{ customerProfile?.name || t('회원 고객') }}</strong>
            <p>{{ (!isKoreanLanguage ? customerProfile?.email : (customerProfile?.phone || customerProfile?.email)) || t('회원 정보로 상담을 연결합니다.') }}</p>
            <p v-if="!isKoreanLanguage && !form.contact_email" class="mt-1 text-amber-700">{{ t('한국어 외 상담에는 회원 이메일이 필요합니다.') }}</p>
          </div>
          <template v-else>
            <label class="block text-xs font-medium text-stone-600">{{ t('이름') }} <span class="text-red-600">{{ t('필수') }}</span>
              <iconic-input
                v-model:value="form.customer_name"
                wrapper-class="mt-1 overflow-hidden rounded-lg border border-stone-200"
                maxlength="120"
                autocomplete="name"
                :placeholder="t('이름')"
              />
            </label>
            <p class="text-xs text-stone-500">{{ isKoreanLanguage ? t('휴대폰이나 이메일 중 하나를 입력해 주세요.') : t('한국어 외 상담에서는 이메일을 입력해 주세요.') }}</p>
            <iconic-input
              v-if="isKoreanLanguage"
              v-model:value="form.contact_phone"
              type="tel"
              wrapper-class="overflow-hidden rounded-lg border border-stone-200"
              maxlength="30"
              autocomplete="tel"
              :placeholder="t('휴대폰')"
            />
            <iconic-input
              v-model:value="form.contact_email"
              type="email"
              wrapper-class="overflow-hidden rounded-lg border border-stone-200"
              maxlength="255"
              autocomplete="email"
              :placeholder="t('이메일')"
            />
          </template>
          <iconic-textarea
            v-model:value="form.message"
            maxlength="4000"
            class="!min-h-[7rem] !resize-none !rounded-lg !border !border-stone-200 text-sm w-full"
            :placeholder="t('무엇을 도와드릴까요?')"
          />
          <div>
            <check-box-input
              :value="form.notification_consent"
              @update:value="form.notification_consent = $event"
            >
              {{ t('채팅 이탈 후 답변 알림 수신에 동의합니다.') }}
            </check-box-input>
          </div>
          <button class="cw-chat-primary w-full" :disabled="sending || !canStart" @click="startConversation">{{ t('상담 시작') }}</button>
        </div>
        <div v-if="capability?.mobile_app?.android_install_url || capability?.mobile_app?.ios_install_url" class="border-t pt-3 text-sm">
          <p class="mb-2 text-stone-600">{{ t('앱을 설치하면 답변을 푸시 알림으로 받을 수 있습니다.') }}</p>
          <div class="flex gap-2">
            <a v-if="capability.mobile_app.android_install_url" class="cw-chat-link" :href="capability.mobile_app.android_install_url">{{ t('Android 앱') }}</a>
            <a v-if="capability.mobile_app.ios_install_url" class="cw-chat-link" :href="capability.mobile_app.ios_install_url">{{ t('iPhone 앱') }}</a>
          </div>
        </div>
      </div>

      <template v-else>
        <div v-if="conversation.continued_from" data-testid="customer-chat-continued-from" class="border-b border-blue-200 bg-blue-50 px-4 py-2 text-center text-sm text-blue-800">
          {{ t('이전 상담에서 이어진 문의입니다.') }}
        </div>
        <div v-if="conversation.assignment_pending" data-testid="customer-chat-assignment-pending" class="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-800">{{ t('상담사를 배정중입니다.') }}</div>
        <div ref="messageArea" data-testid="customer-chat-message-area" class="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50 max-h-[50vh]">
          <div
            v-for="message in messages"
            :key="message.id"
            class="flex items-end gap-1"
            :class="message.sender_type === 'customer' ? 'justify-end' : 'justify-start'"
          >
            <div
              data-testid="customer-chat-message-meta"
              class="flex shrink-0 flex-col pb-1 text-[10px] text-stone-500"
              :class="message.sender_type === 'customer' ? 'order-first items-end' : 'order-last items-start'"
            >
              <span v-if="message.sender_type === 'customer' && !message.recipient_read_at" data-testid="customer-chat-unread-status">{{ t('읽지않음') }}</span>
              <span data-testid="customer-chat-message-created-at" class="whitespace-nowrap">{{ formatDate(message.created_at) }}</span>
            </div>
            <div data-testid="customer-chat-message-bubble" class="max-w-[82%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap" :class="message.sender_type === 'customer' ? 'bg-stone-800 text-white' : 'bg-white border'">
              <a v-if="message.attachment" :href="attachmentUrl(message)" target="_blank" rel="noopener noreferrer" class="block">
                <img
                  data-testid="customer-chat-message-image"
                  :src="attachmentUrl(message)"
                  :alt="t('상담 이미지')"
                  class="max-h-72 max-w-full rounded-xl object-contain"
                  loading="lazy"
                  @load="scrollToBottom"
                >
              </a>
              <div v-for="(part, index) in messageParts(message)" :key="`${message.id}-${part.type}-${part.locale || index}`" :class="index ? 'mt-2 border-t border-current/20 pt-2' : ''">
                <span v-if="messageParts(message).length > 1" class="mb-0.5 block text-[10px] opacity-60">{{ part.type === 'original' ? t('원문') : t('번역') }} · {{ part.locale }}</span>
                <span>{{ part.text }}</span>
              </div>
            </div>
          </div>
          <CustomerChatTypingIndicator
            v-if="administratorTyping"
            :label="t('상담원이 답변을 작성하고 있습니다.')"
          />
        </div>
        <div v-if="isClosed" class="p-4 border-t space-y-3">
          <div v-if="ratingSubmitted" data-testid="customer-chat-rating-thank-you" class="py-3 text-center">
            <div class="text-4xl" aria-hidden="true">💛</div>
            <p class="mt-3 font-semibold text-stone-800">{{ t('소중한 평가를 남겨주셔서 감사합니다.') }}</p>
            <p class="mt-1 text-sm text-stone-500">{{ t('더 나은 상담으로 보답하겠습니다.') }}</p>
            <button type="button" data-testid="customer-chat-rating-close" class="cw-chat-primary mt-4" @click="closePanel">{{ t('채팅창 닫기') }}</button>
          </div>
          <template v-else>
            <p class="text-sm">{{ t('상담이 종료되었습니다.') }}</p>
            <div data-testid="customer-chat-continue" class="space-y-2 rounded-xl border border-stone-200 bg-stone-50 p-3">
              <strong class="text-sm text-stone-800">{{ t('이 상담에 이어서 문의하기') }}</strong>
              <p class="text-xs text-stone-500">
                {{ conversation.rated_at ? t('평가가 끝난 상담은 이전 상담과 연결된 새 상담으로 시작합니다.') : t('메시지를 보내면 이 상담이 다시 진행 상태로 복원됩니다.') }}
              </p>
              <iconic-textarea
                v-model:value="continuationDraft"
                maxlength="4000"
                class="!min-h-[4.5rem] w-full !resize-none rounded-lg !border !border-stone-200 text-sm"
                :placeholder="t('이어서 문의할 내용을 입력해 주세요.')"
              />
              <input ref="continuationImageInput" type="file" accept="image/jpeg,image/png,image/webp" class="hidden" @change="continueWithImage">
              <div class="flex justify-end gap-2">
                <button type="button" class="rounded border border-stone-300 px-3 py-2 text-xs" :disabled="sending" @click="chooseContinuationImage">{{ t('이미지') }}</button>
                <button type="button" class="cw-chat-primary" :disabled="sending || !continuationDraft.trim()" @click="continueConversation()">{{ sending ? t('전송 중...') : t('상담 계속하기') }}</button>
              </div>
            </div>
            <div v-if="!conversation.rated_at" class="space-y-2">
              <iconic-textarea
                v-model:value="ratingComment"
                maxlength="1000"
                class="min-h-[6rem] w-full !resize-none rounded-lg !border !border-stone-200 text-sm"
                :placeholder="t('상담 평을 남겨주세요 (선택)')"
              />
              <div class="flex justify-between items-start gap-2">
                <div class="rounded-lg border border-stone-200 bg-stone-50 p-3 w-full">
                  <p class="mb-2 text-xs font-medium text-stone-600">{{ t('상담 만족도') }}</p>
                  <star-input v-model:value="rating">{{ t('{rating}점', {rating}) }}</star-input>
                </div>
                <button class="cw-chat-primary whitespace-nowrap" :disabled="submittingRating" @click="submitRating">{{ submittingRating ? t('평가 제출 중...') : t('평가 제출') }}</button>
              </div>
            </div>
            <p v-else class="text-sm text-stone-600">{{ t('평가해 주셔서 감사합니다. ({rating}점)', {rating: conversation.rating}) }}</p>
          </template>
        </div>
        <div v-else class="p-3 border-t flex gap-2">
          <input ref="imageInput" data-testid="customer-chat-image-input" type="file" accept="image/jpeg,image/png,image/webp" class="hidden" @change="sendImage">
          <div class="min-w-0 flex-1">
            <iconic-textarea
              v-model:value="draft"
              maxlength="4000"
              class="!min-h-[4.5rem] !resize-none !rounded-lg !border !border-stone-200 text-sm w-full"
              :placeholder="t('메시지 입력')"
              @keydown="handleDraftKeydown"
              @focus="handleDraftFocus"
              @blur="handleDraftBlur"
            />
          </div>
          <div class="flex flex-col gap-1">
            <button type="button" class="rounded border border-stone-300 px-3 py-2 text-xs text-stone-600 disabled:opacity-50" :disabled="sending" @click="chooseImage">{{ t('이미지') }}</button>
            <button class="cw-chat-primary" :disabled="sending" @click="sendMessage">{{ t('전송') }}</button>
            <button class="text-xs text-stone-500" @click="closeConversation">{{ t('상담 종료') }}</button>
          </div>
        </div>
      </template>
      <p v-if="error" class="px-4 py-2 text-xs text-red-600 bg-red-50">{{ error }}</p>
    </section>
  </div>
</template>

<style scoped>
.cw-chat-panel { height: 100vh; min-height: 100vh; max-height: 100vh; }
.cw-chat-primary { @apply rounded-lg bg-stone-800 text-white px-4 py-2 text-sm disabled:opacity-50; }
.cw-chat-link { @apply rounded-lg border border-stone-300 px-3 py-2 text-xs; }

@supports (height: 100dvh) {
    .cw-chat-panel { height: 100dvh; min-height: 100dvh; max-height: 100dvh; }
}

@media (min-width: 640px) {
    .cw-chat-panel {
        height: min(90vh, 900px);
        min-height: min(90vh, 900px);
        max-height: min(90vh, 900px);
    }
}

.cw-chat-panel--preview {
    height: auto;
    min-height: 0;
    max-height: none;
}
</style>
